/**
 * Equipment-catalog BASELINE entries — the platform-wide seed set for the ONE
 * canonical OEM/equipment database (`equipment_catalog`).
 *
 * Extracted from `services/supplyChain/equipmentCatalogStore.ts` so the seed
 * CLI (`scripts/ops/seedOemEquipmentCatalog.ts`, firebase-admin) can build the
 * exact same rows WITHOUT importing the client Firebase SDK. The store
 * re-exports `baselineEquipmentCatalogEntries` so existing callers are
 * unchanged.
 *
 * Three source families fuse here:
 *  1. planning archetypes (`LEAD_TIME_BASELINES`) — BTM/BOP/IT SKUs;
 *  2. the OEM generation source register (research-grade OEM products,
 *     mapped via `oemGenerationEquipmentCatalogEntries`);
 *  3. the G&I screening catalog (the solver's vendor envelopes) — mapped as
 *     browsable rows carrying `generationScreeningId` + the screening
 *     `catalogVersion` so a future live read path can join back to the exact
 *     envelope revision (solver integrity: saved scenarios pin their values).
 */
import type { EquipmentCatalogEntry } from '../../types/supplyChain';
import { LEAD_TIME_BASELINES } from '../supplyChainBaselines';
import { oemGenerationEquipmentCatalogEntries } from './oemGenerationEquipmentCatalog';
import { GENERATION_EQUIPMENT_CATALOG } from '../generationImpact/generationEquipmentCatalog';

const TAG_BY_KEY: Record<string, string[]> = {
  'smr-module': ['btm', 'nuclear', 'power-generation'],
  'gas-turbine': ['btm', 'gas-turbine', 'power-generation'],
  'gsu-transformer': ['bop', 'grid-interconnect', 'power-conversion'],
  'hv-switchgear': ['bop', 'grid-interconnect', 'power-conversion'],
  'cooling-tower': ['bop', 'cooling'],
  'chiller-plant': ['bop', 'cooling'],
  'ups-module': ['bop', 'data-center-it'],
  'server-rack': ['data-center-it'],
  'long-haul-fiber': ['connectivity', 'grid-interconnect'],
};

const REACTOR_VENDOR_BY_KEY: Record<string, string[]> = {
  'smr-module-nuscale-class': ['nuscale-voygr'],
  'smr-module-x-energy-class': ['xe-100'],
};

/** Seed from LEAD_TIME_BASELINES — idempotent upsert. */
export function baselineEquipmentCatalogEntries(): EquipmentCatalogEntry[] {
  const planningBaselines = LEAD_TIME_BASELINES.map((b) => ({
    id: `catalog-${b.key}`,
    name: b.name,
    spec: b.defaultSpec,
    scmCategory: b.category,
    tags: TAG_BY_KEY[b.key] ?? [b.category.replace(/-/g, '_')],
    ...(REACTOR_VENDOR_BY_KEY[b.key] ? { reactorVendorIds: REACTOR_VENDOR_BY_KEY[b.key] } : {}),
    indicativeUnitCostUsd: b.unitCostUsd ?? undefined,
    leadTimeMonths: { low: b.leadTimeMonthsLow, high: b.leadTimeMonthsHigh },
    source: 'baseline' as const,
  }));
  return [...planningBaselines, ...oemGenerationEquipmentCatalogEntries()];
}

/**
 * The G&I SCREENING envelopes as browsable catalog rows. These are the exact
 * entries the generation solver applies (`applyGenerationEquipmentEnvelope`);
 * the row carries the screening id + catalogVersion as the JOIN KEY — the
 * numeric duty values themselves stay in the versioned screening catalog so a
 * catalog edit can never silently change a saved scenario's meaning.
 */
export function generationScreeningCatalogEntries(): EquipmentCatalogEntry[] {
  return GENERATION_EQUIPMENT_CATALOG.map((entry) => ({
    id: `catalog-generation-screening-${entry.catalogId}`,
    name: `${entry.manufacturer} ${entry.model}`,
    spec: [
      entry.trainConfiguration,
      `${entry.publishedRating.outputMWPerBlock} MW ${entry.publishedRating.ratingScope.replaceAll('-', ' ')} per block`,
      'Generation & Impact screening envelope (solver vendor basis)',
    ].join(' · '),
    scmCategory: 'power-generation' as const,
    tags: [
      'generation-screening',
      entry.technology,
      entry.manufacturer.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-'),
    ],
    capacityRating: `${entry.publishedRating.outputMWPerBlock} MW/block`,
    powerPerUnitMw: entry.publishedRating.outputMWPerBlock,
    source: 'baseline' as const,
    // Join keys for the future live G&I read path (extra fields are preserved
    // by Firestore and ignored by parseCatalogDoc until the schema learns them).
    ...( {
      generationScreeningId: entry.catalogId,
      generationCatalogVersion: entry.catalogVersion,
    } as unknown as Partial<EquipmentCatalogEntry>),
  }));
}

/**
 * ASSUMED placeholders for the non-gas technologies the G&I technology picker
 * offers but the screening catalog has no sourced envelope for (SMR/nuclear,
 * solar+BESS, wind+BESS, standalone BESS). Explicitly labelled ASSUMED so
 * nothing fabricated reads as authoritative; real values are entered through
 * the existing admin Equipment-catalog editor.
 */
export function assumedGenerationPlaceholderEntries(): EquipmentCatalogEntry[] {
  const placeholders: Array<{ key: string; name: string; technology: string }> = [
    { key: 'smr-envelope', name: 'SMR power block (envelope pending)', technology: 'smr-nuclear' },
    { key: 'solar-bess-envelope', name: 'Solar + BESS block (envelope pending)', technology: 'solar-bess' },
    { key: 'wind-bess-envelope', name: 'Wind + BESS block (envelope pending)', technology: 'wind-bess' },
    { key: 'bess-envelope', name: 'Standalone BESS block (envelope pending)', technology: 'battery-storage' },
  ];
  return placeholders.map((p) => ({
    id: `catalog-generation-assumed-${p.key}`,
    name: p.name,
    spec: 'ASSUMED placeholder — no sourced OEM envelope yet. Enter sourced values via Admin → Equipment catalog before relying on this technology in generation screening.',
    scmCategory: 'power-generation' as const,
    tags: ['generation-screening', 'assumed', p.technology],
    source: 'baseline' as const,
    ...({ assumed: true } as unknown as Partial<EquipmentCatalogEntry>),
  }));
}
